<template>
  <div class="blog-page">
    <div class="blog-wrapper">
      <!-- Featured Blog Post -->
      <div class="featured-post" v-if="allBlogPosts.length > 0">
        <NuxtLink
          :to="localePath(`/${allBlogPosts[0].slug}`)"
          class="featured-content"
        >
          <div class="featured-image-wrapper">
            <NuxtImg
              v-if="allBlogPosts[0].image"
              :src="cdnUrl + allBlogPosts[0].image"
              :alt="allBlogPosts[0].title"
              fetchpriority="high"
              class="featured-image"
              loading="eager"
            />
          </div>
          <div class="featured-text">
            <div class="featured-categories">
              <span
                v-for="category in allBlogPosts[0].categories"
                :key="category"
                class="featured-category-tag"
              >
                {{ category.toUpperCase() }}
              </span>
            </div>
            <h2 class="featured-title">{{ allBlogPosts[0].title }}</h2>
            <p class="featured-excerpt">{{ allBlogPosts[0].excerpt }}</p>
            <div class="featured-read-more">
              <span>{{ $t("blog.readMore") }}</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
        </NuxtLink>
      </div>

      <!-- Blog Grid with Newsletter -->
      <div class="blog-content-wrapper">
        <div class="blog-grid">
          <BlogCard
            v-for="post in paginatedPosts"
            :key="post.slug"
            :slug="post.slug"
            :title="post.title"
            :excerpt="post.excerpt"
            :image="post.image"
            :author="post.author"
            :date="post.date"
            :categories="post.categories"
            :readTime="post.readTime"
          />
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <div class="pagination">
          <button
            class="pagination-btn pagination-prev"
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            :aria-label="$t('pagination.prev')"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 16L6 10L12 4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'pagination-btn',
              'pagination-number',
              { active: page === currentPage },
            ]"
            :aria-label="$t('pagination.goToPage', { page })"
            :aria-current="page === currentPage ? 'page' : undefined"
          >
            {{ page }}
          </button>

          <button
            class="pagination-btn pagination-next"
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            :aria-label="$t('pagination.next')"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 4L14 10L8 16"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import allBlogPostsRaw from "~/data/blogPosts.json";
import { seoIndex } from "~/data/seoIndex.js";

const { locale, t } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl ?? "";

// Locale-aware canonical URL
const canonicalUrl = computed(
  () => `${config.public.baseUrl}${localePath("/")}`,
);

const allBlogPosts = computed(() =>
  allBlogPostsRaw.filter(
    (p) => p.lang === locale.value || (!p.lang && locale.value === "en"),
  ),
);

const postsPerPage = 10;
const currentPage = ref(1);

watch(locale, () => {
  currentPage.value = 1;
});

const totalPages = computed(() => {
  const len = allBlogPosts.value.length;
  if (len <= 1) return 1;
  return Math.ceil((len - 1) / postsPerPage);
});

const paginatedPosts = computed(() => {
  if (allBlogPosts.value.length <= 1) return [];
  const start = (currentPage.value - 1) * postsPerPage;
  const end = start + postsPerPage;
  return allBlogPosts.value.slice(1).slice(start, end);
});

const visiblePages = computed(() => {
  const pages = [];
  const maxVisible = 5;

  if (totalPages.value <= maxVisible) {
    // Show all pages if total is less than max
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    // Calculate range around current page
    let start = Math.max(2, currentPage.value - 1);
    let end = Math.min(totalPages.value - 1, currentPage.value + 1);

    // Adjust range if at beginning or end
    if (currentPage.value <= 3) {
      end = 4;
    } else if (currentPage.value >= totalPages.value - 2) {
      start = totalPages.value - 3;
    }

    // Add pages in range
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Always show last page
    if (!pages.includes(totalPages.value)) {
      pages.push(totalPages.value);
    }
  }

  return pages;
});

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    if (import.meta.client) window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const seo = computed(() => seoIndex[locale.value] ?? seoIndex.en);

useHreflang({
  en: "/en/",
  fr: "/fr/",
  es: "/es/",
  de: "/de/",
});

useSeoMeta({
  title: () => seo.value.title,
  description: () => seo.value.description,
  keywords: () => seo.value.keywords,
  ogTitle: () => seo.value.ogTitle,
  ogDescription: () => seo.value.ogDescription,
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogImage: `${cdnUrl}/static/images/homepage-background.png`,
  twitterCard: "summary_large_image",
  twitterTitle: () => seo.value.twitterTitle,
  twitterDescription: () => seo.value.twitterDescription,
  twitterImage: `${cdnUrl}/static/images/homepage-background.png`,
});

useHead(() => ({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        name: seo.value.schemaName,
        description: seo.value.schemaDescription,
        url: canonicalUrl.value,
        publisher: {
          "@type": "Organization",
          name: "Travvir",
          logo: {
            "@type": "ImageObject",
            url: `${cdnUrl}/static/images/homepage-background.png`,
          },
        },
      }),
    },
  ],
}));
</script>

<style lang="scss" scoped>
.blog-page {
  margin-top: 100px;
  font-family: $theme-font;
  background: transparent;
  min-height: 100vh;
  .blog-wrapper {
    max-width: 1200px;
    width: 100%;
    margin: auto;
    margin-top: 2rem;
    padding: 0 2rem;
  }

  .blog-hero {
    text-align: center;
    padding: 4rem 0 3rem;

    @include respond-below(sm) {
      padding: 3rem 0 2rem;
    }

    h1 {
      font-family: $theme-font;
      font-size: $text-xxxl;
      font-weight: $fw-semibold;
      line-height: 1.2;
      color: rgba(255, 255, 255, 0.95);
      margin-bottom: 1rem;

      @include respond-below(md) {
        font-size: $text-xxl;
      }

      @include respond-below(sm) {
        font-size: $text-xl;
      }
    }

    p {
      font-family: $theme-font;
      font-size: $text-md;
      line-height: 1.6;
      color: rgba(255, 255, 255, 0.7);
      max-width: 700px;
      margin: 0 auto;

      @include respond-below(sm) {
        font-size: $text-sm;
      }
    }
  }

  .featured-post {
    margin-bottom: 3rem;

    @include respond-below(md) {
      margin-bottom: 2rem;
    }

    .featured-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
      background: rgba(255, 255, 255, 0.03);
      border-radius: $radius-lg;
      overflow: hidden;
      text-decoration: none;
      transition: all 0.3s ease;
      border: 1px solid rgba(255, 255, 255, 0.08);

      @include respond-below(md) {
        grid-template-columns: 1fr;
        gap: 0;
      }

      &:hover {
        border-color: rgba(255, 255, 255, 0.15);

        .featured-image {
          transform: scale(1.05);
        }
      }

      .featured-image-wrapper {
        width: 100%;
        height: 100%;
        min-height: 300px;
        max-height: 400px;
        overflow: hidden;
        background: #1a1a1a;

        @include respond-below(md) {
          min-height: 200px;
          max-height: 200px;
        }

        .featured-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
      }

      .featured-text {
        padding: 2.5rem;
        display: flex;
        flex-direction: column;
        justify-content: center;

        @include respond-below(md) {
          padding: 2rem;
        }

        @include respond-below(sm) {
          padding: 1.5rem;
        }

        .featured-categories {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;

          .featured-category-tag {
            font-size: 0.7rem;
            font-weight: $fw-semibold;
            letter-spacing: 0.05em;
            color: rgba(255, 255, 255, 0.5);
            padding: 0.35rem 0.85rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: $radius-sm;
          }
        }

        .featured-title {
          font-size: 2rem;
          font-weight: $fw-semibold;
          line-height: 1.3;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 1rem;

          @include respond-below(lg) {
            font-size: 1.75rem;
          }

          @include respond-below(md) {
            font-size: 1.3rem;
          }
        }

        .featured-excerpt {
          font-size: $text-sm;
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 1.5rem;
          @include respond-below(md) {
            font-size: $text-xs;
            display: none;
          }
        }

        .featured-read-more {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #5f87ed;
          font-weight: $fw-medium;
          font-size: $text-sm;

          svg {
            transition: transform 0.3s ease;
          }
        }
      }
    }
  }

  .blog-content-wrapper {
    // display: grid;
    // grid-template-columns: 1fr 380px;
    gap: 2.5rem;
    padding-bottom: 4rem;

    @include respond-below(lg) {
      // grid-template-columns: 1fr;
      gap: 2rem;
    }

    .blog-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;

      @include respond-below(xl) {
        grid-template-columns: repeat(2, 1fr);
      }

      @include respond-below(md) {
        grid-template-columns: 1fr;
      }
    }

    .newsletter-section {
      @include respond-below(lg) {
        max-width: 500px;
        margin: 0 auto;
      }

      .newsletter-box {
        background: linear-gradient(135deg, #5f87ed 0%, #4a6fd4 100%);
        border-radius: $radius-lg;
        padding: 2.5rem;
        position: sticky;
        top: 2rem;

        @include respond-below(sm) {
          padding: 2rem;
        }

        h3 {
          font-size: 1.5rem;
          font-weight: $fw-semibold;
          color: #fff;
          margin-bottom: 1rem;
          line-height: 1.3;
        }

        p {
          font-size: $text-sm;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 1.5rem;
        }

        .newsletter-form {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;

          input {
            padding: 0.875rem 1rem;
            border: none;
            border-radius: $radius-sm;
            font-size: $text-sm;
            background: rgba(255, 255, 255, 0.95);
            color: #000;
            outline: none;

            &::placeholder {
              color: rgba(0, 0, 0, 0.5);
            }

            &:focus {
              background: #fff;
            }
          }

          .subscribe-btn {
            padding: 0.875rem 1rem;
            background: #fff;
            color: #5f87ed;
            border: none;
            border-radius: $radius-sm;
            font-weight: $fw-semibold;
            font-size: $text-sm;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover {
              background: rgba(255, 255, 255, 0.9);
              transform: translateY(-2px);
            }
          }
        }
      }
    }
  }

  .pagination-wrapper {
    display: flex;
    justify-content: center;
    padding: 3rem 0 4rem;

    @include respond-below(sm) {
      padding: 2rem 0 3rem;
    }
  }

  .pagination {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .pagination-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
      height: 40px;
      padding: 0 0.5rem;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: $radius-sm;
      color: rgba(255, 255, 255, 0.7);
      font-size: $text-sm;
      font-weight: $fw-medium;
      cursor: pointer;
      transition: all 0.3s ease;
      font-family: $theme-font;

      @include respond-below(sm) {
        min-width: 36px;
        height: 36px;
        font-size: $text-xs;
      }

      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.9);
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      &.pagination-number {
        &.active {
          background: #5f87ed;
          border-color: #5f87ed;
          color: #fff;
        }
      }

      &.pagination-prev,
      &.pagination-next {
        svg {
          width: 20px;
          height: 20px;

          @include respond-below(sm) {
            width: 16px;
            height: 16px;
          }
        }
      }
    }
  }
}
</style>
